import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import * as localforage from 'localforage'
import { nanoid } from 'nanoid/non-secure'
import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import * as yup from 'yup'

import {
  ExpenseAnalysisResponse,
  SerializableExpenseDetection,
  SerializableGeometry
} from '../../../sdk'
import { CenteredLoading } from '../../CenteredLoading/CenteredLoading'
import UploadPreview from './UploadPreview'

interface CacheableResults {
  image: Blob
  analysisResults: ExpenseAnalysisResponse
}

interface DocumentAnalysisResultsUiState {
  analysisResults: ExpenseAnalysisResponse
  selectedBlock: string | null
}

type DocumentAnalysisUiState =
  | {
      step: 'initial'
    }
  | {
      step: 'upload'
    }
  | {
      step: 'results'
      resultsUiState: DocumentAnalysisResultsUiState | null
    }

interface LineItemGroup {
  index: number
  rows: Array<Record<string, string>>
  // Elements of a row are IDs of key-value pairs of detections. Either key ID or value ID may be blank.
  detections: Record<string, SerializableExpenseDetection | null> // Mapping of ID to detection
}

interface LabelValuePair {
  label: SerializableExpenseDetection | null
  value: SerializableExpenseDetection | null
  labelId: string
  valueId: string
}

interface SummaryFieldGroup {
  id: string
  types: Array<string>
  detections: LabelValuePair[]
  // Each SummaryFieldGroup has only one "row" of label-value pairs.
}

interface SummaryFieldsByGroup {
  groups: Array<SummaryFieldGroup>
  ungroupedFields: LabelValuePair[]
  idToGeometry: Record<string, SerializableGeometry>
}

export interface ExpectedTableFillSuggestionRow {
  Name: string
  'Part Number': string
  Brand: string
  'Unit Cost': string
  'Unit Core Cost': string
  Quantity: string
}

const expectedTableFillSuggestionSchema = yup.array().of(
  yup.object().shape({
    Name: yup.string(),
    'Part Number': yup.string(),
    Brand: yup.string(),
    'Unit Cost': yup.string(),
    'Unit Core Cost': yup.string(),
    Quantity: yup.string()
  })
)

interface Props {
  fillSuggestedRowsCallback: (
    rows: ExpectedTableFillSuggestionRow[],
    autoAdded: boolean
  ) => void
}

const DocumentAnalysis: FC<Props> = ({ fillSuggestedRowsCallback }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [uiState, setUiState] = useState<DocumentAnalysisUiState>({
    step: 'initial'
  })
  const imageRef = useRef<Blob | null>(null)

  const analysisSuccessCallback: (
    image: Blob,
    analysisResults: ExpenseAnalysisResponse
  ) => Promise<void> = async (image, analysisResults) => {
    const cacheableResults: CacheableResults = {
      image,
      analysisResults
    }
    await localforage.setItem('documentAnalysisCache', cacheableResults)
    setUiState({
      step: 'results',
      resultsUiState: {
        analysisResults,
        selectedBlock: null
      }
    })
    imageRef.current = image
  }

  const checkCacheAndLoadResults = useCallback(async () => {
    const cachedResults: CacheableResults | null = await localforage.getItem(
      'documentAnalysisCache'
    )
    if (cachedResults) {
      setUiState({
        step: 'results',
        resultsUiState: {
          analysisResults: cachedResults.analysisResults,
          selectedBlock: null
        }
      })
      imageRef.current = cachedResults.image
    } else {
      setUiState({ step: 'upload' })
      imageRef.current = null
    }
  }, [])

  useEffect(() => {
    checkCacheAndLoadResults()
  }, [checkCacheAndLoadResults])

  const handleGotoUpload = () => {
    setUiState({ step: 'upload' })
  }

  const analysisResults =
    uiState.step === 'results' && uiState.resultsUiState?.analysisResults

  const lineItemGroups: LineItemGroup[] | null = useMemo(() => {
    if (!analysisResults) {
      return null
    }

    return analysisResults.expenseDocuments!.flatMap((document) =>
      document.lineItemGroups!.map((group) => {
        const idToDetection: Record<string, SerializableExpenseDetection> = {}
        const rows: Array<Record<string, string>> = []

        group.lineItemFields!.forEach((lineItem) => {
          const labelValuePairIds: Record<string, string> = {}
          lineItem.forEach((field) => {
            const labelId = nanoid()
            const valueId = nanoid()
            labelValuePairIds[labelId] = valueId
            idToDetection[labelId] = field.labelDetection!
            idToDetection[valueId] = field.valueDetection!
          })
          rows.push(labelValuePairIds)
        })

        return {
          index: group.lineItemGroupIndex!,
          rows,
          detections: idToDetection
        }
      })
    )
  }, [analysisResults])

  const summaryFieldGroups: SummaryFieldsByGroup | null = useMemo(() => {
    if (!analysisResults) {
      return null
    }

    const summaryFieldsByGroupByDocument =
      analysisResults.expenseDocuments!.map((document) => {
        const fieldsByGroup: SummaryFieldsByGroup = {
          groups: [],
          ungroupedFields: [],
          idToGeometry: {}
        }
        const idToGroup: Map<string, SummaryFieldGroup> = new Map()

        document.summaryFields!.forEach((field) => {
          const labelId = nanoid()
          const valueId = nanoid()
          if (field.labelDetection) {
            fieldsByGroup.idToGeometry[labelId] =
              field.labelDetection!.geometry!
          }
          if (field.valueDetection) {
            fieldsByGroup.idToGeometry[valueId] =
              field.valueDetection?.geometry!
          }

          if (field.groupProperties!.length === 0) {
            fieldsByGroup.ungroupedFields.push({
              label: field.labelDetection || null,
              value: field.valueDetection || null,
              labelId,
              valueId
            })
          }

          field.groupProperties!.forEach((group) => {
            const groupId = group.id!
            if (!idToGroup.has(groupId)) {
              idToGroup.set(groupId, {
                id: groupId,
                types: group.types!,
                detections: []
              })
            }

            idToGroup.get(groupId)!.detections.push({
              label: field.labelDetection || null,
              value: field.valueDetection || null,
              labelId,
              valueId
            })
          })
        })

        for (const group of idToGroup.values()) {
          fieldsByGroup.groups.push(group)
        }

        return fieldsByGroup
      })

    return {
      groups: summaryFieldsByGroupByDocument.flatMap(({ groups }) => groups),
      ungroupedFields: summaryFieldsByGroupByDocument.flatMap(
        ({ ungroupedFields }) => ungroupedFields
      ),
      idToGeometry: summaryFieldsByGroupByDocument.reduce(
        (accum, { idToGeometry }) => ({
          ...accum,
          ...idToGeometry
        }),
        {}
      )
    }
  }, [analysisResults])

  const drawImageOnCanvas = useCallback(async () => {
    if (uiState.step === 'results') {
      const canvas = canvasRef.current!
      const bitmap = await createImageBitmap(imageRef.current!)
      const scaledWidth = bitmap.width > 500 ? 500 : bitmap.width
      const scaledHeight = Math.round(
        (bitmap.height * scaledWidth) / bitmap.width
      )
      canvas.height = scaledHeight
      canvas.width = scaledWidth
      const context = canvas.getContext('2d')
      context!.drawImage(bitmap, 0, 0, scaledWidth, scaledHeight)

      const selectedBlockId = uiState.resultsUiState?.selectedBlock
      if (selectedBlockId) {
        const box =
          summaryFieldGroups?.idToGeometry[selectedBlockId]?.boundingBox ||
          lineItemGroups?.find((group) => group.detections[selectedBlockId])
            ?.detections[selectedBlockId]?.geometry?.boundingBox
        if (box) {
          context!.strokeStyle = 'black'
          context!.lineWidth = 2
          context!.strokeRect(
            box.left! * canvas.width,
            box.top! * canvas.height,
            box.width! * canvas.width,
            box.height! * canvas.height
          )
        }
      }
    }
  }, [lineItemGroups, summaryFieldGroups?.idToGeometry, uiState])

  useEffect(() => {
    drawImageOnCanvas()
  }, [drawImageOnCanvas])

  const tableFillSuggestion = useMemo(() => {
    if (!analysisResults || !analysisResults.fillTableResponse) {
      return null
    }

    try {
      const jsonFromChatGpt = JSON.parse(analysisResults.fillTableResponse)
      expectedTableFillSuggestionSchema.validateSync(jsonFromChatGpt)
      return jsonFromChatGpt as ExpectedTableFillSuggestionRow[]
    } catch {
      return null
    }
  }, [analysisResults])

  useEffect(() => {
    if (tableFillSuggestion) {
      fillSuggestedRowsCallback(tableFillSuggestion, true)
    }
  }, [fillSuggestedRowsCallback, tableFillSuggestion])

  const setSelectedBlock: (blockId: string | null) => void = (blockId) => {
    setUiState((prevState) => {
      if (prevState.step === 'results' && prevState.resultsUiState) {
        return {
          ...prevState,
          resultsUiState: {
            ...prevState.resultsUiState,
            selectedBlock: blockId
          }
        }
      }
      return prevState
    })
  }

  const getContent = () => {
    switch (uiState.step) {
      case 'initial':
        return <CenteredLoading />
      case 'upload':
        return (
          <UploadPreview analysisSuccessCallback={analysisSuccessCallback} />
        )
      default:
        return (
          <>
            <Typography variant="h3" sx={{ marginBottom: 1 }}>
              Document Analysis Results
            </Typography>
            <Grid container spacing={1}>
              <Grid item md={12} lg={6}>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="h5" component="h4">
                    Document Image
                  </Typography>
                  <Button onClick={handleGotoUpload} variant="outlined">
                    Choose Another Document
                  </Button>
                </Stack>
                <canvas ref={canvasRef} />
              </Grid>
              <Grid item md={12} lg={6}>
                <Stack spacing={1} sx={{ marginBottom: 1 }}>
                  <Typography variant="h5" component="h4">
                    Detected Line Item Tables
                  </Typography>
                  {lineItemGroups ? (
                    <Stack spacing={1}>
                      {lineItemGroups.map(
                        ({ index: lineItemGroupIndex, rows, detections }) => (
                          <Fragment key={lineItemGroupIndex}>
                            {rows.map((row, index) => (
                              <TableContainer
                                sx={{ border: 1, borderColor: 'grey.400' }}
                                key={index}>
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      {Object.keys(row).map((labelId) => (
                                        <TableCell
                                          key={labelId}
                                          onMouseOver={() =>
                                            setSelectedBlock(labelId)
                                          }
                                          onMouseOut={() =>
                                            setSelectedBlock(null)
                                          }>
                                          {detections[labelId]?.text ||
                                            '(No label)'}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      {Object.keys(row).map((labelId) => (
                                        <TableCell
                                          key={labelId}
                                          onMouseOver={() =>
                                            setSelectedBlock(row[labelId]!)
                                          }
                                          onMouseOut={() =>
                                            setSelectedBlock(null)
                                          }>
                                          {detections[row[labelId]]?.text ||
                                            '(No value)'}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ))}
                          </Fragment>
                        )
                      )}
                    </Stack>
                  ) : (
                    <Alert color="info">No tables found.</Alert>
                  )}
                </Stack>
                <Stack spacing={1} sx={{ marginBottom: 1 }}>
                  <Typography variant="h5" component="h4">
                    Detected Summary Fields
                  </Typography>
                  {summaryFieldGroups ? (
                    <Stack spacing={1}>
                      {summaryFieldGroups.groups.map((group) => (
                        <Fragment key={group.id}>
                          <Typography variant="h6" component="h5">
                            Types: {group.types.join(', ')}
                          </Typography>
                          <TableContainer
                            sx={{ border: 1, borderColor: 'grey.400' }}>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  {group.detections.map(
                                    ({ label, labelId }) => (
                                      <TableCell
                                        key={labelId}
                                        onMouseOver={() =>
                                          setSelectedBlock(labelId)
                                        }
                                        onMouseOut={() =>
                                          setSelectedBlock(null)
                                        }>
                                        {label?.text || '(No label)'}
                                      </TableCell>
                                    )
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  {group.detections.map(
                                    ({ value, valueId }) => (
                                      <TableCell
                                        key={valueId}
                                        onMouseOver={() =>
                                          setSelectedBlock(valueId)
                                        }
                                        onMouseOut={() =>
                                          setSelectedBlock(null)
                                        }>
                                        {value?.text || '(No value)'}
                                      </TableCell>
                                    )
                                  )}
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Fragment>
                      ))}
                    </Stack>
                  ) : (
                    <Alert color="info">No summary fields found.</Alert>
                  )}
                </Stack>
                <Stack spacing={1}>
                  <Typography variant="h5" component="h4">
                    Table Fill Suggestion
                  </Typography>
                  {tableFillSuggestion && tableFillSuggestion.length > 0 ? (
                    <>
                      <TableContainer
                        sx={{ border: 1, borderColor: 'grey.400' }}>
                        <Table>
                          {tableFillSuggestion && (
                            <>
                              <TableHead>
                                <TableRow>
                                  {Object.keys(tableFillSuggestion[0]).map(
                                    (columnLabel) => (
                                      <TableCell key={columnLabel}>
                                        {columnLabel}
                                      </TableCell>
                                    )
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {tableFillSuggestion.map((row, index) => (
                                  <TableRow key={index}>
                                    {Object.keys(row).map((columnLabel) => (
                                      <TableCell key={columnLabel}>
                                        {
                                          row[
                                            columnLabel as keyof ExpectedTableFillSuggestionRow
                                          ]
                                        }
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </>
                          )}
                        </Table>
                      </TableContainer>
                      <Button
                        onClick={() =>
                          fillSuggestedRowsCallback(tableFillSuggestion!, false)
                        }>
                        Add Suggested Rows
                      </Button>
                    </>
                  ) : (
                    <Alert color="info">
                      No table fill suggestion available.
                    </Alert>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </>
        )
    }
  }

  return <Box>{getContent()}</Box>
}

export default DocumentAnalysis
